import {
  createRouter,
  createWebHistory,
  type RouteRecordName,
} from "vue-router";
import { AppIdentifier, AppRouteNames } from "@/shared/@types";
import { getAppByPath, isNotProduction } from "@/shared/helper";
import { usePermission } from "@/shared/composables";
import { isAuthenticated } from "@/app/AuthService";
import { useAuthenticatedUsersOnly } from "@/shared/router";
import { routes } from "./routes";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
});

router.beforeEach(async (to, from) => {
  const userIsAuthenticated = await isAuthenticated();
  const appToVisit = getAppByPath(to.path);

  // ----- Routes with all users allowed ------------------------------------------------------
  const unrestrictedRoutes: RouteRecordName[] = [
    AppRouteNames.GeneralError,
    AppRouteNames.Maintenance,
    AppRouteNames.ExistingUserRegistration, // The existing user registration route is accessible even if the user is logged in
    AppRouteNames.MeterReport, // The permissions are checked inside FBS app route's `beforeEnter`
    AppRouteNames.FbsHubMeterReport, // The permissions are checked inside FBS app route's `beforeEnter`
  ];

  if (unrestrictedRoutes.includes(to.name)) return true;

  // ----- Routes with unauthenticated users only -----------------------------------------------
  const unauthenticatedOnlyRoutes: RouteRecordName[] = [
    AppRouteNames.ThgQuoteUnauthenticatedVehicleRegistration,
    AppRouteNames.Login,
    AppRouteNames.LoginCallback,
  ];

  const isUnauthenticatedOnlyRoute =
    appToVisit === AppIdentifier.Registration ||
    unauthenticatedOnlyRoutes.includes(to.name);

  if (!userIsAuthenticated) {
    // If the user is not authenticated, all routes of the login and registration apps are accessible (this includes /thg-quote/registration)
    if (isUnauthenticatedOnlyRoute) return true;

    // If the user is not authenticated and tries to access a route that requires authentication redirect to login
    return { name: AppRouteNames.Login };
  }

  // ----- App missing or not found -----------------------------------------------------------
  if (!appToVisit) return { name: AppRouteNames.Dashboard };

  // ----- Routes with authenticated users only -----------------------------------------------
  const {
    hasUserData,
    hasFetchingUserError,
    initializeUserData,
    accessRouteWithCustomerRequired,
    needsToCompleteRegistration,
    hasCustomerWithPermission,
  } = useAuthenticatedUsersOnly(to);

  if (!hasUserData.value) await initializeUserData(appToVisit);
  if (hasFetchingUserError.value) return { name: AppRouteNames.GeneralError };

  const { hasPermissionForApp } = usePermission();

  if (
    accessRouteWithCustomerRequired.value &&
    needsToCompleteRegistration.value &&
    typeof to.name === "string"
  ) {
    return {
      name: AppRouteNames.FinishRegistration,
      query: { returnTo: to.fullPath },
    };
  }

  if (
    to.name === AppRouteNames.FinishRegistration &&
    !needsToCompleteRegistration.value
  ) {
    return { name: AppRouteNames.Dashboard };
  }

  // TODO: Remove when FBS is ready for production
  if (
    isNotProduction() &&
    appToVisit === AppIdentifier.FbsHub &&
    hasPermissionForApp(appToVisit)
  )
    return true;

  // TODO: Remove when gas shop is ready for production
  if (
    isNotProduction() &&
    appToVisit === AppIdentifier.GasShop
    // && hasPermissionForApp(appToVisit) -> TODO: readd when customer has service roles
  )
    return true;

  if (!hasPermissionForApp(appToVisit)) {
    // If the user has no permission for the app and comes from wid-services try to set a customer id that has permission
    if (from.name === AppRouteNames.WestfalenIdAccountManagementServices) {
      if (!hasCustomerWithPermission(appToVisit))
        return { name: AppRouteNames.Dashboard };
    } else {
      return { name: AppRouteNames.Dashboard };
    }
  }

  return true;
});

/* Manually scroll to the top of the main content area after each route change,
as the default scroll behavior does not work with the sidebar layout */
router.afterEach(() => {
  document.querySelector(".sidebar-layout--main")?.scrollTo(0, 0);
});

export default router;
